import { Input } from "@/components/ui/input";
import {
  TILE_SIZE,
  type TileLocale,
  type TileSizes,
} from "@/features/obs/lib/tiles";

/**
 * The two tile images a report needs — one per locale.
 *
 * 🔴 THE TWO OBS PAGES CARRY DIFFERENT ARTWORK for the same report. Verified on
 * the live site, where even the source folders differ (`Site_Pages_OBS_Report_`
 * against `_OBS_Reports_`). A single field puts Arabic artwork on the English
 * page.
 *
 * Shared between the create wizard and the edit dialog deliberately: this started
 * as two file inputs written twice, the second of which was never updated when
 * the first gained its English half — so a report created through the wizard got
 * an Arabic tile and silently fell back to it on the English page.
 */
export function ObsTileFields({
  sizes,
  disabled = false,
  current,
  picked,
  onPick,
}: {
  sizes: TileSizes;
  disabled?: boolean;
  /** Tiles already attached, when editing an existing report. */
  current?: {
    ar: { url: string; alt: string } | null;
    en: { url: string; alt: string } | null;
  };
  /**
   * Names of images chosen but not yet sent.
   *
   * ⚠️ A FILE INPUT CANNOT BE REFILLED IN CODE — only a person may set its value.
   * So a wizard draft restored from storage still holds the image while the input
   * beside it reads empty, and without saying which file is attached an intact
   * draft looks like a lost one.
   */
  picked?: Partial<Record<TileLocale, string | null>>;
  onPick: (locale: TileLocale, file: File | null) => void;
}) {
  return (
    <div className="space-y-4">
      {(["ar", "en"] as TileLocale[]).map((locale) => {
        const existing = current?.[locale] ?? null;
        const sz = sizes[locale];
        const exact = sz?.w === TILE_SIZE.w && sz?.h === TILE_SIZE.h;
        return (
          <div
            key={locale}
            className="space-y-2 rounded-lg border border-border p-3"
          >
            <div className="text-sm font-semibold">
              {locale === "ar"
                ? "بطاقة الصفحة العربية"
                : "بطاقة الصفحة الإنجليزية"}
            </div>

            {existing ? (
              <div className="flex items-center gap-3">
                <img
                  src={existing.url}
                  alt={existing.alt}
                  className="size-16 rounded object-cover"
                />
                <p className="text-xs text-muted-foreground">
                  البطاقة الحالية — اختيار صورة جديدة سيستبدلها.
                </p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                الصور المرحّلة في مكتبة الوسائط ضمن{" "}
                <span dir="ltr" className="font-mono">
                  pages-data/OBS-Reports/
                </span>
              </p>
            )}

            <Input
              type="file"
              accept="image/*"
              disabled={disabled}
              onChange={(e) => onPick(locale, e.target.files?.[0] ?? null)}
            />
            {picked?.[locale] && (
              <p className="text-xs text-emerald-600">
                الصورة المختارة:{" "}
                <span dir="ltr" className="font-mono">
                  {picked[locale]}
                </span>
              </p>
            )}
            {sz && (
              <p
                className={`text-xs ${exact ? "text-emerald-600" : "text-amber-600"}`}
              >
                <span dir="ltr" className="font-mono">
                  {sz.w}×{sz.h}
                </span>
                {exact
                  ? " — مطابق للبطاقات الحالية."
                  : ` — يختلف عن ${TILE_SIZE.w}×${TILE_SIZE.h} المعتاد.`}
              </p>
            )}
          </div>
        );
      })}

      <p className="text-xs text-muted-foreground">
        الصفحتان العربية والإنجليزية تستخدمان صورًا مختلفة لكل تقرير. إن تُركت
        الإنجليزية فارغة ستُستخدم الصورة العربية في الصفحة الإنجليزية.
      </p>
    </div>
  );
}
